/**
 * ProviderRow Component
 *
 * Single row displaying provider summary information
 */

import React from 'react';
import { ChevronRight as ChevronIcon, Person as PersonIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { ProviderSummary, TrendDirection } from '../../types';
import { TrendIndicator } from '../shared/TrendIndicator';

interface ProviderRowProps {
  /** Provider data */
  provider: ProviderSummary;
  /** Click handler - receives providerId and optional providerName */
  onClick?: (providerId: string, providerName?: string) => void;
}

export const ProviderRow: React.FC<ProviderRowProps> = ({ provider, onClick }) => {
  const theme = useTheme();

  const displayName = provider.provider_name || provider.user_id.slice(0, 8);

  const handleClick = () => {
    onClick?.(provider.user_id, displayName);
  };
  const compliancePct = provider.compliance_percentage;
  const cptAccuracy = provider.cpt_accuracy_pct;

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        '&:hover': onClick
          ? {
              bgcolor: theme.palette.grey[50],
            }
          : {},
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      {/* Avatar / Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: '50%',
          bgcolor: theme.palette.primary.light + '20',
          color: theme.palette.primary.main,
          flexShrink: 0,
        }}
      >
        <PersonIcon />
      </Box>

      {/* Provider Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {provider.total_notes_audited || 0} notes audited
        </Typography>
      </Box>

      {/* Compliance */}
      <Box sx={{ textAlign: 'center', minWidth: 80 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.25 }}>
          Compliance
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color:
                compliancePct !== null && compliancePct !== undefined
                  ? compliancePct >= 80
                    ? theme.palette.success.main
                    : compliancePct >= 60
                      ? theme.palette.warning.main
                      : theme.palette.error.main
                  : theme.palette.text.disabled,
            }}
          >
            {compliancePct !== null && compliancePct !== undefined
              ? `${compliancePct.toFixed(0)}%`
              : '—'}
          </Typography>
          {provider.compliance_trend && (
            <TrendIndicator trend={provider.compliance_trend as TrendDirection} size="small" />
          )}
        </Box>
      </Box>

      {/* CPT Accuracy */}
      <Box sx={{ textAlign: 'center', minWidth: 80 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 0.25 }}>
          CPT Accuracy
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color:
              cptAccuracy !== null && cptAccuracy !== undefined
                ? cptAccuracy >= 90
                  ? theme.palette.success.main
                  : cptAccuracy >= 70
                    ? theme.palette.warning.main
                    : theme.palette.error.main
                : theme.palette.text.disabled,
          }}
        >
          {cptAccuracy !== null && cptAccuracy !== undefined ? `${cptAccuracy.toFixed(0)}%` : '—'}
        </Typography>
      </Box>

      {/* Chevron */}
      {onClick && <ChevronIcon sx={{ color: theme.palette.grey[400], flexShrink: 0 }} />}
    </Box>
  );
};

export default ProviderRow;
